/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useContext, useState } from 'react';

import { useTheme } from '@blend/alchemy';

import { removeTeamMember } from 'blb-api/groups';
import { useToasts } from 'blb-components/AuthenticatedApp/Toast';
import { AlertBar, Modal } from 'blb-components/GrommetComponents';
import { CenteredLoadingSpinner } from 'blb-components/CenteredLoadingSpinner';
import { BlendBridge } from 'blb-types/blendBridge';
import { getPersona } from 'blb-persona/index';
import { MeContext } from 'blb-contexts/Me';
import { AlertButton, ModalStyles } from './styles';

interface RemoveTeamMemberModalProps {
  handleClose: (shouldRefetch?: boolean) => Promise<void>;
  team: BlendBridge.API.SearchGroupRecord;
  assignment: BlendBridge.API.GroupAssignmentWithName;
}

const RemoveTeamMemberModal: React.FC<RemoveTeamMemberModalProps> = ({ handleClose, team, assignment }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error>();
  const me = useContext(MeContext)!;
  const Toaster = useToasts()!;
  const theme = useTheme();
  const isSelf = assignment.principalId === me.principalId;

  const acceptedTeamAdmins = team.assignments.filter(member => member.role === 'ADMIN' && member.status === 'ACCEPTED');
  const isAssigneeLastAcceptedAdmin =
    assignment.role === 'ADMIN' &&
    acceptedTeamAdmins.length === 1 &&
    acceptedTeamAdmins[0].principalId === assignment.principalId;
  const isAssigneeCentralizedUser = assignment.principalId === team.broadcasterPrincipalId;
  const isAssigneeRemovable = !isAssigneeLastAcceptedAdmin && !isAssigneeCentralizedUser;

  const getModalBody = () => {
    if (isLoading) {
      return <CenteredLoadingSpinner />;
    }

    if (isAssigneeCentralizedUser) {
      return `The user with the team's email cannot be removed. To change the team's email or turn this centralized team into an open team, please create a new team to replace this one.`;
    }

    if (isAssigneeLastAcceptedAdmin) {
      return 'You are the only admin on the team who has accepted their invitation. Another admin must accept their invitation before you can leave.';
    }

    return isSelf
      ? `Are you sure you want to leave team ${team.name}?`
      : `Are you sure you want remove ${assignment.name} from team ${team.name}?`;
  };

  const getModalTitle = () => {
    if (isSelf) {
      return isAssigneeRemovable ? 'Leave team?' : 'Not allowed to leave the team';
    }

    return isAssigneeRemovable ? 'Remove team member?' : 'Not allowed to remove team member';
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError(undefined);
    try {
      await removeTeamMember(team.id, assignment.principalId);
      Toaster.addToast({
        message: isSelf
          ? `Successfully left team ${team.name}.`
          : `${assignment.name} successfully removed from team ${team.name}.`,
        theme: 'info',
      });
      // Not putting this in a finally block to avoid updating state if the modal closes
      setIsLoading(false);
      await handleClose(true);
    } catch (e) {
      const err = e as Error;
      setError(err);
      setIsLoading(false);
    }
  };

  return (
    <Modal
      buttonComponents={
        isAssigneeRemovable && (
          <AlertButton
            color="status-critical"
            disabled={isLoading}
            hoverColor={theme.colors.agnostic.negative[300]}
            id="it-remove-team-member-submit"
            label={isSelf ? 'Leave' : 'Remove'}
            onClick={handleSubmit}
            primary={true}
          />
        )
      }
      handleClose={handleClose}
      id="it-remove-team-member-modal"
      isOpen
      style={ModalStyles}
      title={getModalTitle()}
    >
      {error && (
        <AlertBar
          boxProps={{ margin: { bottom: 'medium' } }}
          errorMessage={`Error removing member. Please try again or contact support at ${
            getPersona(me.principalType).getSupportContactInfo().email
          }`}
          id="it-remove-member-error"
        />
      )}
      {getModalBody()}
    </Modal>
  );
};

export default RemoveTeamMemberModal;
